import React, { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  FileText,
  X,
  ChevronDown,
  AlertCircle,
  Loader2,
  BookOpen,
  Table,
  Droplet,
  Shield,
  Lightbulb,
  Cpu,
  Tag,
  Sparkles,
  CheckCircle2,
  Image as ImageIcon,
} from "lucide-react";
import { documentsAPI, machinesAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";

const typeOptions = [
  { value: "manual", label: "Manual", icon: BookOpen },
  { value: "sop", label: "SOP", icon: FileText },
  { value: "guide", label: "Guide", icon: Lightbulb },
  { value: "troubleshooting", label: "Troubleshooting", icon: AlertCircle },
  { value: "specification", label: "Specification", icon: Table },
  { value: "diagram", label: "Diagram", icon: ImageIcon },
  { value: "safety", label: "Safety", icon: Shield },
  { value: "other", label: "Other", icon: Droplet },
];

/**
 * Custom Dropdown styled strictly matching AddMachine.jsx input aesthetics
 */
const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder = "Select an option...",
  icon: DefaultIcon,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);
  const DisplayIcon = selectedOption?.icon || DefaultIcon;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={clsx(
          "w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border rounded-xl text-xs sm:text-sm text-slate-800 text-left flex items-center justify-between transition-all shadow-sm",
          isOpen
            ? "border-blue-500 ring-2 ring-blue-500/20 bg-white"
            : "border-slate-200"
        )}
      >
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
          {DisplayIcon && <DisplayIcon className="w-4 h-4" />}
        </span>

        <span
          className={clsx(
            "truncate font-medium pr-2",
            selectedOption ? "text-slate-800" : "text-slate-400"
          )}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>

        <ChevronDown
          className={clsx(
            "w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0",
            isOpen && "rotate-180 text-blue-600"
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.99 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 z-50 mt-1.5 bg-white rounded-xl shadow-xl border border-slate-200/90 py-1.5 max-h-56 overflow-y-auto"
          >
            {options.map((option) => {
              const OptionIcon = option.icon;
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={clsx(
                    "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                    isSelected
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-700 hover:bg-slate-50"
                  )}
                >
                  <div className="flex items-center gap-2.5 truncate">
                    {OptionIcon && (
                      <OptionIcon
                        className={clsx(
                          "w-3.5 h-3.5 flex-shrink-0",
                          isSelected ? "text-blue-600" : "text-slate-400"
                        )}
                      />
                    )}
                    <span className="truncate">{option.label}</span>
                  </div>
                  {isSelected && (
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const initialFormState = {
  machineId: "",
  documentType: "manual",
  category: "",
  title: "",
  description: "",
};

const DocumentUpload = ({ isOpen = true, onClose }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [formData, setFormData] = useState(initialFormState);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate("/knowledge");
    }
  };

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormState);
      setFiles([]);
      setDragActive(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  const { data: machines } = useQuery({
    queryKey: ["machines-list"],
    queryFn: () =>
      machinesAPI.getAll({ limit: 100 }).then((res) => {
        const raw = res.data?.data || res.data;
        return Array.isArray(raw) ? raw : [];
      }),
  });

  const uploadMutation = useMutation({
    mutationFn: (data) => documentsAPI.upload(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge"] });
      queryClient.invalidateQueries({ queryKey: ["knowledge-search"] });
      toast.success("Document uploaded successfully! AI processing started.");
      handleClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Upload failed");
    },
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleFiles = (newFiles) => {
    const validTypes = ["application/pdf", "text/plain"];
    const maxSize = 50 * 1024 * 1024;
    const validFiles = newFiles.filter((file) => {
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    if (validFiles.length !== newFiles.length) {
      toast.error(
        "Some files were rejected. Only PDF and TXT files (Max 50MB) are allowed.",
      );
    }

    const fileToUpload = validFiles.length > 0 ? validFiles[0] : null;

    if (fileToUpload) {
      setFiles([
        {
          file: fileToUpload,
          id: Math.random().toString(36).substring(2, 11),
        },
      ]);
      setFormData((prev) => ({
        ...prev,
        title: fileToUpload.name.replace(/\.[^/.]+$/, ""),
      }));
    } else {
      setFiles([]);
    }
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleFiles(droppedFiles);
    },
    [handleFiles],
  );

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
    e.target.value = null;
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (files.length === 0) {
      toast.error("Please select a document to upload");
      return;
    }

    if (!formData.title.trim()) {
      toast.error("Document title is required");
      return;
    }

    const form = new FormData();
    form.append("document", files[0].file);
    form.append("machineId", formData.machineId);
    form.append("documentType", formData.documentType);
    form.append("category", formData.category);
    form.append("title", formData.title || files[0].file.name);
    form.append("description", formData.description);

    uploadMutation.mutate(form);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const machineOptions = [
    { value: "", label: "General (No specific machine)", icon: Cpu },
    ...(machines || []).map((m) => ({
      value: m.id,
      label: `${m.name}${m.model ? ` (${m.model})` : ""}`,
      icon: Cpu,
    })),
  ];

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget && !uploadMutation.isPending) {
            handleClose();
          }
        }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="relative w-full max-w-2xl bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden flex flex-col my-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-5 sm:p-6 border-b border-slate-100 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 border border-blue-100/80 flex items-center justify-center flex-shrink-0">
                <Upload className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight">
                  Upload Technical Document
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Digitize manuals, SOPs, and documentation for the knowledge base
                </p>
              </div>
            </div>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit} className="flex flex-col">
            <div className="p-5 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto max-h-[calc(85vh-130px)]">
              {/* File Dropzone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={clsx(
                  "p-5 border-2 border-dashed transition-all text-center rounded-2xl relative",
                  dragActive
                    ? "border-blue-500 bg-blue-50/70 shadow-sm"
                    : files.length > 0
                      ? "border-emerald-300 bg-emerald-50/30"
                      : "border-slate-200 hover:border-slate-300 bg-slate-50/60"
                )}
              >
                <input
                  type="file"
                  id="modal-file-upload"
                  className="hidden"
                  accept=".pdf,.txt"
                  onChange={handleFileInput}
                />
                <label
                  htmlFor="modal-file-upload"
                  className="cursor-pointer block"
                >
                  <div className="w-12 h-12 mx-auto mb-2.5 rounded-2xl bg-white border border-slate-200/80 flex items-center justify-center text-blue-600 shadow-xs">
                    <Upload className="w-5 h-5" />
                  </div>
                  <p className="text-sm font-semibold text-slate-800 mb-0.5">
                    {dragActive
                      ? "Drop the file to select"
                      : "Drag & drop a file here, or browse"}
                  </p>
                  <p className="text-xs text-slate-400">
                    PDF and TXT supported • Single file upload • Max 50MB
                  </p>
                </label>
              </div>

              {/* Uploaded File Preview Card */}
              {files.length > 0 && (
                <div className="p-3 sm:p-3.5 bg-blue-50/50 border border-blue-100 rounded-xl flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-600 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-slate-900 text-xs sm:text-sm truncate">
                        {files[0].file.name}
                      </p>
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100/80 px-1.5 py-0.5 rounded-md">
                        <CheckCircle2 className="w-3 h-3" />
                        Ready
                      </span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 font-medium">
                      {formatFileSize(files[0].file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(files[0].id)}
                    className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-white rounded-lg transition-colors"
                    title="Remove file"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Row 1: Document Type & Machine Association with custom dropdowns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Document Type <span className="text-rose-500">*</span>
                  </label>
                  <CustomSelect
                    value={formData.documentType}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, documentType: val }))
                    }
                    options={typeOptions}
                    icon={BookOpen}
                    placeholder="Select Document Type"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Machine Association
                  </label>
                  <CustomSelect
                    value={formData.machineId}
                    onChange={(val) =>
                      setFormData((prev) => ({ ...prev, machineId: val }))
                    }
                    options={machineOptions}
                    icon={Cpu}
                    placeholder="General (No specific machine)"
                  />
                </div>
              </div>

              {/* Row 2: Title & Category Tag */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Document Title <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <FileText className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, title: e.target.value }))
                      }
                      placeholder="e.g. ABC Machine Operating Manual v2.0"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                    Category Tag
                  </label>
                  <div className="relative">
                    <Tag className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          category: e.target.value,
                        }))
                      }
                      placeholder="e.g. Hydraulic System, Electrical"
                      className="w-full pl-10 pr-3.5 py-2.5 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Row 3: Description */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Description (Optional)
                </label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Brief summary of document topics, revisions or applicability..."
                  className="w-full p-3 bg-slate-50/60 hover:bg-slate-50 focus:bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm resize-none"
                />
              </div>

              {/* AI Processing Banner */}
              <div className="p-3.5 rounded-xl bg-blue-50/70 border border-blue-100 flex items-start gap-3 text-xs text-blue-900">
                <Sparkles className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-0.5">
                  <p className="font-semibold text-blue-950">
                    Automated AI Knowledge Extraction
                  </p>
                  <p className="text-slate-600 leading-relaxed text-[11px]">
                    After upload, AI will automatically analyze this document, extract key entities & procedures, and make it available for instant troubleshooting assistance.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 sm:p-5 bg-slate-50/80 border-t border-slate-100 flex items-center justify-end gap-2.5">
              <button
                type="button"
                disabled={uploadMutation.isPending}
                onClick={handleClose}
                className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 sm:px-4 hover:border-slate-300 rounded-xl"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={files.length === 0 || uploadMutation.isPending}
                className="btn btn-primary text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20 whitespace-nowrap rounded-xl inline-flex items-center gap-2"
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    <span>Upload Document</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

export default DocumentUpload;
